// mailinator_clone/frontend/src/components/utils/geoUtils.js
import tokml from 'tokml';
import togpx from 'togpx';

/**
 * Auto-detect the separator used in a text string.
 * Tests tab, pipe, semicolon, comma, and multi-space in that priority order.
 * @param {string} text - The raw text to analyze
 * @returns {{ separator: RegExp, separatorName: string, separatorChar: string }}
 */
export const detectSeparator = (text) => {
  const lines = text.split('\n').filter(l => l.trim());
  const testLines = lines.slice(0, Math.min(10, lines.length));

  const candidates = [
    { name: 'tab', char: '\t', regex: /\t/ },
    { name: 'pipe', char: '|', regex: /\|/ },
    { name: 'semicolon', char: ';', regex: /;/ },
    { name: 'comma', char: ',', regex: /,/ },
  ];

  for (const c of candidates) {
    const counts = testLines.map(l => (l.match(new RegExp(c.regex.source, 'g')) || []).length);
    // If every line has at least 1 occurrence and the count is consistent
    if (counts.every(n => n >= 1)) {
      return { separator: new RegExp(`\\s*${c.regex.source}\\s*`), separatorName: c.name, separatorChar: c.char };
    }
  }

  // Fallback: whitespace (2+ spaces or mixed)
  return { separator: /\s+/, separatorName: 'whitespace', separatorChar: ' ' };
};

/**
 * Auto-detect column roles from headers and/or data values.
 * @param {string[]} headers - Column headers (can be empty strings if no header row)
 * @param {string[][]} sampleRows - A few rows of data to analyze
 * @returns {{ latIdx: number, lonIdx: number, timeIdx: number, speedIdx: number, headingIdx: number, altIdx: number, detectedRoles: object }}
 */
export const autoDetectColumns = (headers, sampleRows) => {
  const roles = { lat: -1, lon: -1, time: -1, speed: -1, heading: -1, altitude: -1 };
  const normalHeaders = headers.map(h => h.trim().toLowerCase().replace(/[^a-z0-9]/g, ''));

  // ── Step 1: Match by header name ──
  const HEADER_PATTERNS = {
    lat: ['lat', 'latitude', 'y', 'lattitude', 'lati'],
    lon: ['lon', 'lng', 'longitude', 'long', 'x', 'longi'],
    time: ['time', 'timestamp', 'datetime', 'date', 'ts', 'utc', 'epoch', 'createdat', 'createdon'],
    speed: ['speed', 'velocity', 'spd', 'speedkmh', 'speedmph', 'speedms', 'vehiclespeed'],
    heading: ['heading', 'bearing', 'course', 'direction', 'hdg', 'azimuth'],
    altitude: ['alt', 'altitude', 'elevation', 'elev', 'height', 'z'],
  };

  for (const [role, patterns] of Object.entries(HEADER_PATTERNS)) {
    const idx = normalHeaders.findIndex(h => patterns.includes(h));
    if (idx !== -1) roles[role] = idx;
  }

  // ── Step 2: If lat/lon not found by name, try heuristic on numeric columns ──
  if (roles.lat === -1 || roles.lon === -1) {
    const numericCols = [];
    if (sampleRows.length > 0) {
      for (let col = 0; col < sampleRows[0].length; col++) {
        const vals = sampleRows.map(r => parseFloat(r[col])).filter(v => !isNaN(v));
        if (vals.length >= sampleRows.length * 0.7) {
          const min = Math.min(...vals);
          const max = Math.max(...vals);
          numericCols.push({ col, min, max, vals });
        }
      }
    }

    // Find lat candidate: values within [-90, 90]
    // Find lon candidate: values within [-180, 180] but some outside [-90, 90]
    if (roles.lat === -1) {
      const latCandidates = numericCols.filter(c => c.min >= -90 && c.max <= 90 && roles.lon !== c.col);
      if (latCandidates.length > 0) roles.lat = latCandidates[0].col;
    }
    if (roles.lon === -1) {
      const lonCandidates = numericCols.filter(c => c.min >= -180 && c.max <= 180 && c.col !== roles.lat);
      if (lonCandidates.length > 0) {
        // Prefer column that has values outside [-90,90] range
        const outsideRange = lonCandidates.find(c => c.min < -90 || c.max > 90);
        roles.lon = outsideRange ? outsideRange.col : lonCandidates[lonCandidates.length > 1 ? 1 : 0].col;
      }
    }

    // If still no lat found, take first numeric col that looks like lat
    if (roles.lat === -1 && numericCols.length >= 2) {
      roles.lat = numericCols[0].col;
      roles.lon = numericCols[1].col;
    }

    // Detect speed: numeric column with typical speed range [0-300]
    if (roles.speed === -1) {
      const speedCand = numericCols.find(c => c.col !== roles.lat && c.col !== roles.lon && c.min >= 0 && c.max <= 400);
      if (speedCand) roles.speed = speedCand.col;
    }

    // Detect time: column with non-numeric values that look like timestamps
    if (roles.time === -1 && sampleRows.length > 0) {
      for (let col = 0; col < sampleRows[0].length; col++) {
        if (col === roles.lat || col === roles.lon || col === roles.speed) continue;
        const vals = sampleRows.map(r => r[col]);
        const looksLikeTime = vals.filter(v => !isNaN(Date.parse(v)) || /\d{10,13}/.test(v)).length;
        if (looksLikeTime >= vals.length * 0.7) {
          roles.time = col;
          break;
        }
      }
    }
  }

  const detectedRoles = {};
  Object.entries(roles).forEach(([role, idx]) => {
    if (idx !== -1) detectedRoles[idx] = role;
  });

  return {
    latIdx: roles.lat,
    lonIdx: roles.lon,
    timeIdx: roles.time,
    speedIdx: roles.speed,
    headingIdx: roles.heading,
    altIdx: roles.altitude,
    detectedRoles,
  };
};

/**
 * Parses raw coordinate string into an array of [longitude, latitude] pairs.
 * Auto-detects separator (comma, tab, semicolon, pipe, whitespace).
 * Handles both "lat,lon" and "lon,lat" formats.
 *
 * @param {string} coordinateString - The raw string of coordinates.
 * @returns {Array<Array<number>>} An array of [longitude, latitude] arrays.
 */
export const parseCoordinates = (coordinateString) => {
  const coordinates = [];
  const lines = coordinateString.split('\n').filter(line => line.trim() !== '');
  if (lines.length === 0) return coordinates;

  // Auto-detect separator
  const { separator } = detectSeparator(coordinateString);

  for (const line of lines) {
    const parts = line.trim().split(separator).filter(p => p.trim() !== '');

    if (parts.length >= 2) {
      let lat = parseFloat(parts[0]);
      let lon = parseFloat(parts[1]);

      // Basic validation for numbers
      if (isNaN(lat) || isNaN(lon)) {
        console.warn(`Skipping invalid coordinate line: ${line}`);
        continue;
      }

      // Heuristic to detect lon,lat vs lat,lon
      if (lat < -90 || lat > 90) {
        if (lon >= -180 && lon <= 180) {
          [lat, lon] = [lon, lat];
          console.warn(`Swapped lat/lon for: ${line}. Assumed lon,lat input.`);
        }
      }

      // Final check for valid ranges
      if (lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        coordinates.push([lon, lat]); // GeoJSON is [longitude, latitude]
      } else {
        console.warn(`Skipping out-of-range coordinate: ${line} (lat: ${lat}, lon: ${lon})`);
      }
    }
  }
  return coordinates;
};

/**
 * Converts an array of [lon, lat] coordinates to a GeoJSON LineString or MultiPoint.
 * @param {Array<Array<number>>} coordinates - Array of [longitude, latitude] pairs.
 * @returns {object | null} A GeoJSON LineString object or MultiPoint if only one coordinate.
 */
export const coordinatesToGeoJSON = (coordinates) => {
  if (!coordinates || coordinates.length === 0) {
    return null;
  }

  if (coordinates.length === 1) {
    return {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: coordinates[0]
        },
        properties: {
          name: 'Waypoint 1'
        }
      }]
    };
  }

  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: coordinates
      },
      properties: {
        name: 'GPS Route'
      }
    }]
  };
};

/**
 * Validates if a string is a valid GeoJSON object.
 * @param {string} jsonString - The string to validate.
 * @returns {{isValid: boolean, parsed: object|null, error: string|null}} Validation result.
 */
export const validateAndParseGeoJSON = (jsonString) => {
  if (!jsonString.trim()) {
    return { isValid: false, parsed: null, error: "Input is empty." };
  }
  try {
    const parsed = JSON.parse(jsonString);
    // Basic GeoJSON structure check
    if (!parsed || !parsed.type) {
      throw new Error("Invalid GeoJSON structure: Missing 'type' property.");
    }
    // More robust checks could be added here for specific GeoJSON types
    return { isValid: true, parsed: parsed, error: null };
  } catch (e) {
    return { isValid: false, parsed: null, error: `Invalid JSON or GeoJSON: ${e.message}` };
  }
};

/**
 * Exports GeoJSON to a file.
 * @param {object} geojsonData - The GeoJSON object.
 * @param {string} format - 'geojson', 'kml', or 'gpx'.
 * @param {string} filename - Base filename without extension.
 */
export const exportGeoJSON = (geojsonData, format, filename) => {
  if (!geojsonData) return;

  let content = '';
  let fileExtension = '';
  let mimeType = '';

  try {
    switch (format) {
      case 'geojson':
        content = JSON.stringify(geojsonData, null, 2);
        fileExtension = 'geojson';
        mimeType = 'application/json';
        break;
      case 'kml':
        content = tokml(geojsonData, {
          documentName: filename,
          documentDescription: 'Exported from Spatial Tools',
          name: 'name', // Use 'name' property from GeoJSON features if available
          description: 'description' // Use 'description' property
        });
        fileExtension = 'kml';
        mimeType = 'application/vnd.google-earth.kml+xml';
        break;
      case 'gpx':
        content = togpx(geojsonData, {
          creator: 'Spatial Tools App',
          name: 'name',
          description: 'description'
        });
        fileExtension = 'gpx';
        mimeType = 'application/gpx+xml';
        break;
      default:
        console.error('Unsupported export format:', format);
        return;
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${fileExtension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

  } catch (e) {
    console.error(`Error exporting to ${format}:`, e);
    alert(`Failed to export to ${format}. Ensure your data is valid for this conversion.`);
  }
};

// Helper for file reading
export const readFileAsText = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = (event) => reject(event.target.error);
    reader.readAsText(file);
  });
};

/**
 * Universal file parser: auto-detects format (GeoJSON, JSON, CSV, TSV, plain coords)
 * and returns a normalized FeatureCollection.
 * 
 * @param {string} text - Raw file text
 * @returns {{ geoJson: object|null, format: string, meta: object, error: string|null }}
 *   - geoJson: Normalized FeatureCollection
 *   - format: 'geojson' | 'csv' | 'coordinates'
 *   - meta: { headers, detectedSeparator, detectedColumns, pointCount, hasTimestamps, ... }
 *   - error: error message or null
 */
export const parseAnyGeoFile = (text) => {
  if (!text || !text.trim()) {
    return { geoJson: null, format: null, meta: {}, error: 'Empty input' };
  }

  const trimmed = text.trim();

  // ── Try JSON / GeoJSON first ──
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      let fc;

      if (parsed.type === 'FeatureCollection') {
        fc = parsed;
      } else if (parsed.type === 'Feature') {
        fc = { type: 'FeatureCollection', features: [parsed] };
      } else if (parsed.type && parsed.coordinates) {
        // Raw geometry
        fc = { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: parsed, properties: {} }] };
      } else if (Array.isArray(parsed)) {
        // Array of features or coordinates
        if (parsed.length > 0 && parsed[0].type === 'Feature') {
          fc = { type: 'FeatureCollection', features: parsed };
        } else if (parsed.length > 0 && Array.isArray(parsed[0]) && parsed[0].length >= 2) {
          // Array of coordinate arrays → LineString
          fc = {
            type: 'FeatureCollection',
            features: [{
              type: 'Feature',
              geometry: { type: 'LineString', coordinates: parsed },
              properties: {},
            }],
          };
        }
      }

      if (fc && fc.features) {
        const hasTimestamps = fc.features.some(f =>
          f.properties?.timestamp || f.properties?.time || f.properties?.datetime
        );
        return {
          geoJson: fc,
          format: 'geojson',
          meta: {
            pointCount: fc.features.length,
            geometryTypes: [...new Set(fc.features.map(f => f.geometry?.type).filter(Boolean))],
            hasTimestamps,
            propertyKeys: fc.features[0]?.properties ? Object.keys(fc.features[0].properties) : [],
          },
          error: null,
        };
      }
    } catch (e) {
      // Not valid JSON, fall through to CSV/coords parsing
    }
  }

  // ── Try CSV / TSV ──
  const lines = trimmed.split('\n').filter(l => l.trim());
  if (lines.length >= 2) {
    const csvResult = parseCSVCoordinates(trimmed);
    if (csvResult.coords.length > 0) {
      // Build FeatureCollection from CSV
      const features = csvResult.coords.map((coord, i) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: coord },
        properties: {
          ...csvResult.properties[i],
          ...(csvResult.timestamps[i] ? { timestamp: csvResult.timestamps[i] } : {}),
        },
      }));

      return {
        geoJson: { type: 'FeatureCollection', features },
        format: 'csv',
        meta: {
          headers: csvResult.headers,
          detectedSeparator: csvResult.detectedSeparator,
          detectedColumns: csvResult.detectedColumns,
          hasHeaderRow: csvResult.hasHeaderRow,
          pointCount: features.length,
          hasTimestamps: csvResult.timestamps.length > 0,
          latIdx: csvResult.latIdx,
          lonIdx: csvResult.lonIdx,
          timeIdx: csvResult.timeIdx,
          speedIdx: csvResult.speedIdx,
        },
        error: null,
      };
    }
  }

  // ── Try plain coordinates ──
  const coords = parseCoordinates(trimmed);
  if (coords.length > 0) {
    const features = coords.length === 1
      ? [{ type: 'Feature', geometry: { type: 'Point', coordinates: coords[0] }, properties: {} }]
      : [{
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: coords },
        properties: { name: 'Parsed Route', pointCount: coords.length },
      }];

    return {
      geoJson: { type: 'FeatureCollection', features },
      format: 'coordinates',
      meta: { pointCount: coords.length, hasTimestamps: false },
      error: null,
    };
  }

  return { geoJson: null, format: null, meta: {}, error: 'Could not parse as GeoJSON, CSV, or coordinates' };
};

// ── NEW UTILITIES FOR GEOSPATIAL WORKBENCH ──────────────────────────────────

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const EARTH_RADIUS_M = 6371000;

/**
 * Haversine distance between two [lon, lat] coordinates in meters.
 */
export const haversineDistance = (coord1, coord2) => {
  const [lon1, lat1] = coord1;
  const [lon2, lat2] = coord2;
  const dLat = (lat2 - lat1) * DEG_TO_RAD;
  const dLon = (lon2 - lon1) * DEG_TO_RAD;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Bearing from coord1 to coord2 in degrees (0-360).
 */
export const calculateBearing = (coord1, coord2) => {
  const [lon1, lat1] = coord1.map(v => v * DEG_TO_RAD);
  const [lon2, lat2] = coord2.map(v => v * DEG_TO_RAD);
  const dLon = lon2 - lon1;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * RAD_TO_DEG) + 360) % 360;
};

/**
 * Speed between two points in km/h given ISO timestamps.
 */
export const calculateSpeed = (coord1, coord2, time1, time2) => {
  const dist = haversineDistance(coord1, coord2);
  const dt = (new Date(time2) - new Date(time1)) / 1000; // seconds
  if (dt <= 0) return 0;
  return (dist / dt) * 3.6; // m/s → km/h
};

/**
 * Detect drift/outlier points from coordinates array.
 * @param {Array} coords - Array of [lon, lat] or features with coordinates
 * @param {object} opts - { maxSpeedKmh, maxDeviationSigma, minDistanceM, timestamps }
 * @returns {{ cleaned: Array, removed: Array<{index, reason, coord}> }}
 */
export const detectDriftPoints = (coords, opts = {}) => {
  const { maxSpeedKmh = 200, maxDeviationSigma = 3, minDistanceM = 2, timestamps = [] } = opts;
  const removed = [];
  const cleaned = [];
  const hasTime = timestamps.length === coords.length;

  // Pass 1: Speed filter
  const afterSpeed = [{ coord: coords[0], origIdx: 0 }];
  for (let i = 1; i < coords.length; i++) {
    if (hasTime) {
      const speed = calculateSpeed(afterSpeed[afterSpeed.length - 1].coord, coords[i], timestamps[afterSpeed[afterSpeed.length - 1].origIdx], timestamps[i]);
      if (speed > maxSpeedKmh) {
        removed.push({ index: i, reason: `Speed ${speed.toFixed(1)} km/h exceeds ${maxSpeedKmh} km/h`, coord: coords[i] });
        continue;
      }
    }
    afterSpeed.push({ coord: coords[i], origIdx: i });
  }

  // Pass 2: Sigma filter on distances
  if (afterSpeed.length > 2) {
    const distances = [];
    for (let i = 1; i < afterSpeed.length; i++) {
      distances.push(haversineDistance(afterSpeed[i - 1].coord, afterSpeed[i].coord));
    }
    const mean = distances.reduce((a, b) => a + b, 0) / distances.length;
    const std = Math.sqrt(distances.reduce((a, d) => a + (d - mean) ** 2, 0) / distances.length);
    const threshold = mean + maxDeviationSigma * std;

    const afterSigma = [afterSpeed[0]];
    for (let i = 1; i < afterSpeed.length; i++) {
      const dist = haversineDistance(afterSigma[afterSigma.length - 1].coord, afterSpeed[i].coord);
      if (dist > threshold && std > 0) {
        removed.push({ index: afterSpeed[i].origIdx, reason: `Distance ${dist.toFixed(1)}m exceeds ${maxDeviationSigma}σ (${threshold.toFixed(1)}m)`, coord: afterSpeed[i].coord });
      } else {
        afterSigma.push(afterSpeed[i]);
      }
    }

    // Pass 3: Min distance filter
    cleaned.push(afterSigma[0].coord);
    for (let i = 1; i < afterSigma.length; i++) {
      const dist = haversineDistance(cleaned[cleaned.length - 1], afterSigma[i].coord);
      if (dist < minDistanceM) {
        removed.push({ index: afterSigma[i].origIdx, reason: `Distance ${dist.toFixed(1)}m below ${minDistanceM}m minimum`, coord: afterSigma[i].coord });
      } else {
        cleaned.push(afterSigma[i].coord);
      }
    }
  } else {
    afterSpeed.forEach(p => cleaned.push(p.coord));
  }

  removed.sort((a, b) => a.index - b.index);
  return { cleaned, removed };
};

/**
 * Convert coordinate between formats.
 * @param {number} lat - Latitude in decimal degrees
 * @param {number} lon - Longitude in decimal degrees
 * @returns {object} All formats
 */
export const convertCoordinate = (lat, lon) => {
  const toDMS = (deg, isLat) => {
    const dir = isLat ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const minFloat = (abs - d) * 60;
    const m = Math.floor(minFloat);
    const s = ((minFloat - m) * 60).toFixed(2);
    return `${d}° ${m}' ${s}" ${dir}`;
  };

  const toDDM = (deg, isLat) => {
    const dir = isLat ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
    const abs = Math.abs(deg);
    const d = Math.floor(abs);
    const m = ((abs - d) * 60).toFixed(4);
    return `${d}° ${m}' ${dir}`;
  };

  // UTM conversion (simplified)
  const toUTM = (lat, lon) => {
    const zone = Math.floor((lon + 180) / 6) + 1;
    const letter = lat >= 0 ? 'N' : 'S';
    const lonOrigin = (zone - 1) * 6 - 180 + 3;
    const latRad = lat * DEG_TO_RAD;
    const lonRad = lon * DEG_TO_RAD;
    const lonOriginRad = lonOrigin * DEG_TO_RAD;

    const N = EARTH_RADIUS_M / Math.sqrt(1 - 0.00669438 * Math.sin(latRad) ** 2);
    const T = Math.tan(latRad) ** 2;
    const C = 0.00673950 * Math.cos(latRad) ** 2;
    const A = Math.cos(latRad) * (lonRad - lonOriginRad);
    const M = EARTH_RADIUS_M * (
      (1 - 0.00669438 / 4 - 3 * 0.00669438 ** 2 / 64) * latRad -
      (3 * 0.00669438 / 8 + 3 * 0.00669438 ** 2 / 32) * Math.sin(2 * latRad) +
      (15 * 0.00669438 ** 2 / 256) * Math.sin(4 * latRad)
    );

    const easting = 500000 + 0.9996 * N * (A + (1 - T + C) * A ** 3 / 6);
    const northing = (lat < 0 ? 10000000 : 0) + 0.9996 * (M + N * Math.tan(latRad) * (A ** 2 / 2));

    return `${zone}${letter} ${Math.round(easting)}E ${Math.round(northing)}N`;
  };

  // Geohash encoding
  const toGeohash = (lat, lon, precision = 9) => {
    const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
    let latRange = [-90, 90];
    let lonRange = [-180, 180];
    let hash = '';
    let bit = 0;
    let ch = 0;
    let isLon = true;

    while (hash.length < precision) {
      const range = isLon ? lonRange : latRange;
      const val = isLon ? lon : lat;
      const mid = (range[0] + range[1]) / 2;
      if (val >= mid) {
        ch |= (1 << (4 - bit));
        range[0] = mid;
      } else {
        range[1] = mid;
      }
      isLon = !isLon;
      if (bit < 4) {
        bit++;
      } else {
        hash += BASE32[ch];
        bit = 0;
        ch = 0;
      }
    }
    return hash;
  };

  return {
    dd: `${lat.toFixed(6)}, ${lon.toFixed(6)}`,
    ddReversed: `${lon.toFixed(6)}, ${lat.toFixed(6)}`,
    dms: `${toDMS(lat, true)}, ${toDMS(lon, false)}`,
    ddm: `${toDDM(lat, true)}, ${toDDM(lon, false)}`,
    utm: toUTM(lat, lon),
    geohash: toGeohash(lat, lon),
    geoJsonArray: `[${lon.toFixed(6)}, ${lat.toFixed(6)}]`,
  };
};

/**
 * Parse various coordinate input formats and return [lat, lon].
 */
export const parseAnyCoordinate = (input) => {
  const s = input.trim();

  // Try DD: "40.7128, -74.0060" or "40.7128 -74.0060"
  const ddMatch = s.match(/^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/);
  if (ddMatch) {
    const a = parseFloat(ddMatch[1]);
    const b = parseFloat(ddMatch[2]);
    if (a >= -90 && a <= 90) return [a, b];
    if (b >= -90 && b <= 90) return [b, a];
    return [a, b];
  }

  // Try DMS: 40° 42' 46.08" N, 74° 0' 21.60" W
  const dmsMatch = s.match(/(\d+)[°]\s*(\d+)[′']\s*([\d.]+)[″"]\s*([NSEW])/gi);
  if (dmsMatch && dmsMatch.length >= 2) {
    const parseDMS = (str) => {
      const m = str.match(/(\d+)[°]\s*(\d+)[′']\s*([\d.]+)[″"]\s*([NSEW])/i);
      if (!m) return NaN;
      let val = parseInt(m[1]) + parseInt(m[2]) / 60 + parseFloat(m[3]) / 3600;
      if (m[4] === 'S' || m[4] === 'W' || m[4] === 's' || m[4] === 'w') val = -val;
      return val;
    };
    const v1 = parseDMS(dmsMatch[0]);
    const v2 = parseDMS(dmsMatch[1]);
    const dir1 = dmsMatch[0].match(/[NSEW]/i)?.[0]?.toUpperCase();
    if (dir1 === 'N' || dir1 === 'S') return [v1, v2];
    return [v2, v1];
  }

  // Try Geohash
  if (/^[0-9b-hjkmnp-z]{4,12}$/i.test(s)) {
    const decoded = decodeGeohash(s);
    if (decoded) return [decoded.lat, decoded.lon];
  }

  return null;
};

/**
 * Decode a geohash string to [lat, lon].
 */
export const decodeGeohash = (hash) => {
  const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
  let latRange = [-90, 90];
  let lonRange = [-180, 180];
  let isLon = true;

  for (const c of hash.toLowerCase()) {
    const idx = BASE32.indexOf(c);
    if (idx === -1) return null;
    for (let bit = 4; bit >= 0; bit--) {
      const range = isLon ? lonRange : latRange;
      const mid = (range[0] + range[1]) / 2;
      if ((idx >> bit) & 1) {
        range[0] = mid;
      } else {
        range[1] = mid;
      }
      isLon = !isLon;
    }
  }
  return {
    lat: (latRange[0] + latRange[1]) / 2,
    lon: (lonRange[0] + lonRange[1]) / 2,
  };
};

/**
 * Parse CSV/TSV string with auto-detected separator and auto-detected columns.
 * @param {string} csvString - The CSV/TSV text
 * @param {object} overrides - Optional { latIdx, lonIdx, timeIdx, speedIdx } to override auto-detection
 * @returns {{ coords, timestamps, properties, headers, detectedSeparator, detectedColumns, hasHeaderRow }}
 */
export const parseCSVCoordinates = (csvString, overrides = {}) => {
  const lines = csvString.trim().split('\n');
  if (lines.length < 2) return { coords: [], timestamps: [], properties: [], headers: [], detectedSeparator: 'comma', detectedColumns: {}, hasHeaderRow: false };

  // Auto-detect separator
  const { separator, separatorName, separatorChar } = detectSeparator(csvString);

  // Split all lines
  const allRows = lines.map(l => l.trim().split(separator).map(p => p.trim()));

  // Detect if first row is a header: if it has more non-numeric values than numeric
  const firstRow = allRows[0];
  const numericInFirst = firstRow.filter(v => !isNaN(parseFloat(v)) && v.trim() !== '').length;
  const hasHeaderRow = numericInFirst < firstRow.length * 0.5;

  const headers = hasHeaderRow ? allRows[0] : allRows[0].map((_, i) => `col_${i}`);
  const dataRows = hasHeaderRow ? allRows.slice(1) : allRows;
  const sampleRows = dataRows.slice(0, Math.min(20, dataRows.length));

  // Auto-detect columns
  const detected = autoDetectColumns(headers, sampleRows);

  // Allow overrides
  const latIdx = overrides.latIdx ?? detected.latIdx;
  const lonIdx = overrides.lonIdx ?? detected.lonIdx;
  const timeIdx = overrides.timeIdx ?? detected.timeIdx;
  const speedIdx = overrides.speedIdx ?? detected.speedIdx;

  if (latIdx === -1 || lonIdx === -1) {
    return { coords: [], timestamps: [], properties: [], headers, detectedSeparator: separatorName, detectedColumns: detected.detectedRoles, hasHeaderRow };
  }

  const coords = [];
  const timestamps = [];
  const properties = [];

  for (const parts of dataRows) {
    const lat = parseFloat(parts[latIdx]);
    const lon = parseFloat(parts[lonIdx]);
    if (isNaN(lat) || isNaN(lon)) continue;
    coords.push([lon, lat]);
    if (timeIdx !== -1 && parts[timeIdx]) timestamps.push(parts[timeIdx]);

    const props = {};
    headers.forEach((h, idx) => {
      if (idx !== latIdx && idx !== lonIdx && parts[idx] !== undefined) {
        const val = parseFloat(parts[idx]);
        props[h] = isNaN(val) ? parts[idx] : val;
      }
    });
    properties.push(props);
  }

  return {
    coords, timestamps, properties, headers,
    detectedSeparator: separatorName,
    detectedColumns: detected.detectedRoles,
    hasHeaderRow,
    latIdx, lonIdx, timeIdx, speedIdx,
  };
};

/**
 * Convert CSV/TSV data to GeoJSON FeatureCollection.
 * Auto-detects separator and column roles.
 * @param {string} csvString - The CSV/TSV text
 * @param {object} options - { geometryType: 'points'|'linestring', overrides }
 * @returns {{ geoJson: object, meta: object }}
 */
export const csvToGeoJSON = (csvString, options = {}) => {
  const { geometryType = 'points', overrides = {} } = options;
  const parsed = parseCSVCoordinates(csvString, overrides);

  if (parsed.coords.length === 0) {
    return { geoJson: null, meta: parsed };
  }

  let geoJson;
  if (geometryType === 'linestring') {
    geoJson = {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: parsed.coords },
        properties: { name: 'Route', pointCount: parsed.coords.length },
      }],
    };
  } else {
    geoJson = {
      type: 'FeatureCollection',
      features: parsed.coords.map((coord, i) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: coord },
        properties: {
          ...parsed.properties[i],
          ...(parsed.timestamps[i] ? { timestamp: parsed.timestamps[i] } : {}),
        },
      })),
    };
  }

  return { geoJson, meta: parsed };
};

/**
 * Calculate deviation between a raw and matched route.
 * Both are arrays of [lon, lat].
 */
export const calculateRouteDeviation = (rawRoute, matchedRoute) => {
  if (!rawRoute.length || !matchedRoute.length) return { deviations: [], stats: {} };

  // For each raw point, find closest matched point
  const deviations = rawRoute.map((rawPt) => {
    let minDist = Infinity;
    let closestIdx = 0;
    matchedRoute.forEach((matchPt, j) => {
      const d = haversineDistance(rawPt, matchPt);
      if (d < minDist) {
        minDist = d;
        closestIdx = j;
      }
    });
    return { rawPoint: rawPt, matchedPoint: matchedRoute[closestIdx], deviation: minDist };
  });

  const devValues = deviations.map(d => d.deviation);
  const totalRaw = rawRoute.reduce((sum, pt, i) => i === 0 ? 0 : sum + haversineDistance(rawRoute[i - 1], pt), 0);
  const totalMatched = matchedRoute.reduce((sum, pt, i) => i === 0 ? 0 : sum + haversineDistance(matchedRoute[i - 1], pt), 0);

  return {
    deviations,
    stats: {
      maxDeviation: Math.max(...devValues),
      avgDeviation: devValues.reduce((a, b) => a + b, 0) / devValues.length,
      medianDeviation: [...devValues].sort((a, b) => a - b)[Math.floor(devValues.length / 2)],
      totalRawDistance: totalRaw,
      totalMatchedDistance: totalMatched,
      pointCount: rawRoute.length,
    },
  };
};

/**
 * Detect stops in a trip (features with speed/time properties).
 * @param {Array} features - GeoJSON Point features with timestamp and optional speed
 * @param {number} speedThreshold - km/h below which is considered stopped
 * @param {number} durationThreshold - seconds minimum to qualify as a stop
 */
export const detectStops = (features, speedThreshold = 3, durationThreshold = 60) => {
  if (!features || features.length < 2) return [];
  const stops = [];
  let stopStart = null;

  for (let i = 0; i < features.length; i++) {
    const f = features[i];
    const speed = f.properties?.speed ?? f.properties?.Speed ?? null;
    const ts = f.properties?.timestamp ?? f.properties?.time ?? f.properties?.datetime ?? null;

    let isIdle = false;
    if (speed !== null && speed !== undefined) {
      isIdle = speed < speedThreshold;
    } else if (i > 0 && ts) {
      const prevTs = features[i - 1].properties?.timestamp ?? features[i - 1].properties?.time ?? null;
      if (prevTs) {
        const calcSpeed = calculateSpeed(
          features[i - 1].geometry.coordinates,
          f.geometry.coordinates,
          prevTs, ts
        );
        isIdle = calcSpeed < speedThreshold;
      }
    }

    if (isIdle) {
      if (!stopStart) stopStart = i;
    } else {
      if (stopStart !== null) {
        const startTs = features[stopStart].properties?.timestamp ?? features[stopStart].properties?.time ?? null;
        const endTs = features[i - 1].properties?.timestamp ?? features[i - 1].properties?.time ?? null;
        const duration = (startTs && endTs) ? (new Date(endTs) - new Date(startTs)) / 1000 : 0;
        if (duration >= durationThreshold || (!startTs && (i - stopStart) >= 3)) {
          stops.push({
            startIndex: stopStart,
            endIndex: i - 1,
            coordinate: features[stopStart].geometry.coordinates,
            duration,
            pointCount: i - stopStart,
          });
        }
        stopStart = null;
      }
    }
  }
  // Handle trailing stop
  if (stopStart !== null) {
    const startTs = features[stopStart].properties?.timestamp ?? features[stopStart].properties?.time ?? null;
    const endTs = features[features.length - 1].properties?.timestamp ?? features[features.length - 1].properties?.time ?? null;
    const duration = (startTs && endTs) ? (new Date(endTs) - new Date(startTs)) / 1000 : 0;
    if (duration >= durationThreshold) {
      stops.push({
        startIndex: stopStart,
        endIndex: features.length - 1,
        coordinate: features[stopStart].geometry.coordinates,
        duration,
        pointCount: features.length - stopStart,
      });
    }
  }
  return stops;
};



/**
 * Douglas-Peucker route simplification.
 * @param {Array<Array<number>>} points - Array of [lon, lat] coordinates.
 * @param {number} tolerance - Tolerance in meters.
 * @returns {Array<Array<number>>} Simplified points.
 */
export const simplifyRoute = (points, tolerance) => {
  if (points.length <= 2) return points;

  const sqTolerance = tolerance * tolerance;

  const getSqSegDist = (p, p1, p2) => {
    let x = p1[0], y = p1[1], dx = p2[0] - x, dy = p2[1] - y;
    if (dx !== 0 || dy !== 0) {
      let t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);
      if (t > 1) {
        x = p2[0]; y = p2[1];
      } else if (t > 0) {
        x += dx * t; y += dy * t;
      }
    }
    dx = p[0] - x; dy = p[1] - y;
    return dx * dx + dy * dy;
  };

  const simplifyStep = (points, first, last, sqTolerance, simplified) => {
    let maxSqDist = sqTolerance, index;
    for (let i = first + 1; i < last; i++) {
      const sqDist = getSqSegDist(points[i], points[first], points[last]);
      if (sqDist > maxSqDist) {
        index = i;
        maxSqDist = sqDist;
      }
    }
    if (maxSqDist > sqTolerance) {
      if (index - first > 1) simplifyStep(points, first, index, sqTolerance, simplified);
      simplified.push(points[index]);
      if (last - index > 1) simplifyStep(points, index, last, sqTolerance, simplified);
    }
  };

  const result = [points[0]];
  simplifyStep(points, 0, points.length - 1, (tolerance / 111000) ** 2, result);
  result.push(points[points.length - 1]);
  return result;
};

/**
 * Calculate bounding box from coordinates.
 * @param {Array<Array<number>>} coords - Array of [lon, lat] pairs.
 * @returns {object} { minLat, maxLat, minLon, maxLon }
 */
export const calculateBBox = (coords) => {
  if (!coords || coords.length === 0) return null;
  let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
  coords.forEach(([lon, lat]) => {
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });
  return { minLat, maxLat, minLon, maxLon };
};

/**
 * Convert BBox object to GeoJSON Polygon.
 */
export const bboxToPolygon = (bbox) => {
  if (!bbox) return null;
  const { minLat, maxLat, minLon, maxLon } = bbox;
  return {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [[
        [minLon, minLat],
        [maxLon, minLat],
        [maxLon, maxLat],
        [minLon, maxLat],
        [minLon, minLat]
      ]]
    },
    properties: { name: 'Bounding Box' }
  };
};

// ── NEW UTILITIES FOR GEOJSON TOOLKIT ──────────────────────────────────────

/**
 * Encode coordinates into a Google Polyline string.
 * @param {Array<Array<number>>} coords - Array of [lon, lat] pairs.
 * @returns {string} The encoded polyline string.
 */
export const encodePolyline = (coords) => {
  const encode = (num) => {
    num = Math.round(num * 1e5);
    num = num < 0 ? ~(num << 1) : num << 1;
    let encoded = '';
    while (num >= 0x20) {
      encoded += String.fromCharCode((0x20 | (num & 0x1f)) + 63);
      num >>= 5;
    }
    encoded += String.fromCharCode(num + 63);
    return encoded;
  };

  let lastLat = 0;
  let lastLon = 0;
  let result = '';

  for (const [lon, lat] of coords) {
    result += encode(lat - lastLat);
    result += encode(lon - lastLon);
    lastLat = lat;
    lastLon = lon;
  }

  return result;
};

/**
 * Decode a Google Polyline string into [lon, lat] coordinates.
 * @param {string} encoded - The polyline string.
 * @returns {Array<Array<number>>} Array of [lon, lat] pairs.
 */
export const decodePolyline = (encoded) => {
  const points = [];
  let index = 0, len = encoded.length;
  let lat = 0, lon = 0;

  while (index < len) {
    let b, shift = 0, result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlon = ((result & 1) ? ~(result >> 1) : (result >> 1));
    lon += dlon;

    points.push([lon / 1e5, lat / 1e5]);
  }
  return points;
};

/**
 * Calculates cumulative distances for an array of points.
 * @param {Array<Array<number>>} points - Array of [lon, lat] coordinates.
 * @returns {Array<number>} Array of cumulative distances in meters.
 */
export const calculateCumulativeDistances = (points) => {
  const distances = [0];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversineDistance(points[i - 1], points[i]);
    distances.push(total);
  }
  return distances;
};